import { CareerSiteBrandConfigurationResponse } from 'api-clients/monolith';
import {
  ActiveFilters,
  getDefaultActiveFilters,
  getSortOptions,
  Segment,
} from 'contexts/app-context';
import queryString from 'query-string';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import {
  allFilterOption,
  currentLocationFilterOption,
} from 'utils/filter-options';

type Option = { key: string; label: string };

const getBrandSettingDefaultLocation = (
  defaultLocationFilter: string | null,
  intl: IntlShape,
): Option => {
  return defaultLocationFilter === 'all'
    ? allFilterOption(intl)
    : currentLocationFilterOption(intl);
};

const createFilterDataFromParams = (
  params: queryString.ParsedQuery,
  brandConfig: CareerSiteBrandConfigurationResponse,
  intl: IntlShape,
): ActiveFilters => {
  const filterData: ActiveFilters = {
    ...getDefaultActiveFilters(intl),
    location: getBrandSettingDefaultLocation(
      brandConfig.settings?.default_location_filter,
      intl,
    ),
  };

  // Map filter keys to their corresponding option sources
  const filterOptionSources: Record<keyof ActiveFilters, Option[] | undefined> =
    {
      compensationType: brandConfig?.compensation_type_options,
      sortBy: getSortOptions(intl),
      radius: brandConfig?.radius_options,
      category: brandConfig?.category_options,
      department: brandConfig?.departments,
      location: brandConfig?.locations,
      position: brandConfig?.positions,
    };

  (Object.keys(filterOptionSources) as (keyof ActiveFilters)[]).forEach(
    filterKey => {
      const options = filterOptionSources[filterKey];
      const paramValue = params[filterKey];

      if (paramValue && options) {
        const match = options.find(opt => opt.key === paramValue);
        if (match) {
          filterData[filterKey] = match;
        }
      }
    },
  );

  // Special case override for `location = all`
  if (params.location === 'all') {
    filterData.location = allFilterOption(intl);
  }

  return filterData;
};

interface useUrlFiltersProps {
  brandConfig: CareerSiteBrandConfigurationResponse | undefined;
  search: string;
  activeFilters: ActiveFilters;
  setActiveFilters: (filters: ActiveFilters) => void;
  currentCity: string | null | undefined;
  setPage: (page: number) => void;
  setPlaceId: Dispatch<SetStateAction<string | null>>;
  setPosition: Dispatch<SetStateAction<Segment>>;
  setIsJobsFromCurrentLocation: Dispatch<SetStateAction<boolean>>;
}

export const useUrlFilters = ({
  brandConfig,
  search,
  activeFilters,
  setActiveFilters,
  currentCity,
  setPage,
  setPlaceId,
  setPosition,
  setIsJobsFromCurrentLocation,
}: useUrlFiltersProps) => {
  const intl = useIntl();
  const lastProcessedFiltersRef = useRef<string>('');

  // Memoize the current filters to prevent unnecessary updates
  const currentFiltersString = useMemo(() => {
    return JSON.stringify(activeFilters);
  }, [activeFilters]);

  useEffect(() => {
    if (brandConfig) {
      const params = queryString.parse(search);

      const filterData = createFilterDataFromParams(params, brandConfig, intl);

      // Don't overwrite position if it's already correctly set (prevents double calls)
      const updatedFilters = {
        ...filterData,
        location: {
          ...filterData.location,
          label: filterData.location.label ?? currentCity,
        },
      };

      // Only update position if it's not already correctly set
      if (params.position && activeFilters.position.key !== params.position) {
        // Position will be handled by PaginatedFilter when data loads
      } else {
        // Keep the current position to prevent overwriting
        updatedFilters.position = activeFilters.position;
      }

      // Only update filters if they've actually changed
      const updatedFiltersString = JSON.stringify(updatedFilters);
      if (
        updatedFiltersString !== currentFiltersString &&
        updatedFiltersString !== lastProcessedFiltersRef.current
      ) {
        setActiveFilters(updatedFilters);
        lastProcessedFiltersRef.current = updatedFiltersString;
      }

      // Set page from URL parameter
      if (params.page) {
        const pageNumber = parseInt(params.page as string, 10);
        if (!Number.isNaN(pageNumber) && pageNumber > 0) {
          setPage(pageNumber);
        }
      }

      const placeId = params.place_id as string;
      if (placeId) {
        setPlaceId(placeId);
      } else {
        setPlaceId(null);
      }

      if (params.position) {
        // For positions from usePositions hook, only set position state if it's not already set
        // This prevents double processing when the URL change was caused by handleFilterChange
        if (activeFilters.position.key !== params.position) {
          setPosition({ id: params.position as string, value: null });
        }
      }

      if (Object.keys(params).length !== 0) {
        setIsJobsFromCurrentLocation(false);
      }
    }
  }, [
    brandConfig,
    search,
    setActiveFilters,
    currentCity,
    setPage,
    setPlaceId,
    setPosition,
    setIsJobsFromCurrentLocation,
    intl,
    currentFiltersString,
    activeFilters.position,
  ]);
};
